import * as React from 'react'
import { inject, observer } from 'mobx-react'
import { CurrentCompanyStore } from '../../../../../stores/CurrentCompanyStore'
import { API, INestedProduct, IInventory } from '@getgreenline/homi-shared'
import { Modal, Alert, Button, message, Skeleton } from 'antd'
import { parseErrorMsg } from '../../../../../utilities/helpers'
import { BLAZE_COMPANY } from '../../../../../constants/general'

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  product?: INestedProduct
  productName: string
  isVariant: boolean
  hasVariants: boolean
  isLoading: boolean
  submit: () => void
  onClose: () => void
  isVisible: boolean
}

interface State {
  numberOfProductInventory: number
  isCheckingInventory: boolean
}

@inject('currentCompanyStore')
@observer
export class DeleteWarningModal extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      isCheckingInventory: true,
      numberOfProductInventory: 0,
    }
  }

  componentDidMount = async () => {
    await this.checkInventory(this.props.product!)
  }

  checkInventory = async (product: INestedProduct) => {
    try {
      this.setState({ isCheckingInventory: true })
      const company = this.props.currentCompanyStore!.company!
      const inventoryList = await API.getInventoryByProduct(company.id, product.id)
      if (this.props.hasVariants) {
        for (const child in product.childProducts) {
          const childInventoryList = await API.getInventoryByProduct(
            company.id,
            product.childProducts[child].id,
          )
          inventoryList.push(...childInventoryList)
        }
      }
      const numberOfProductInventory = inventoryList.reduce(
        (sumOfQuantities: number, inventory: IInventory) => {
          return sumOfQuantities + inventory.quantity
        },
        0,
      )
      this.setState({ numberOfProductInventory })
    } catch (error) {
      message.error(
        parseErrorMsg(error) || 'An error occured while loading invertories of the product',
      )
    } finally {
      this.setState({ isCheckingInventory: false })
    }
  }

  get warningMessages() {
    const { hasVariants } = this.props
    return this.state.numberOfProductInventory ? (
      <div style={{ marginTop: 10 }}>
        <Alert
          type='warning'
          message='Inventory'
          description='This product has inventory. Please clear the inventory before deleting this product.'
          showIcon
        />
      </div>
    ) : (
      <>
        <div style={{ marginTop: 10 }}>
          <Alert
            type='error'
            message='Inventory'
            description={
              <>
                <hr />
                <li>Your current inventory (and lots) will be set to 0.</li>
                <li>Your inventory logs will not be searchable.</li>
                <li>
                  Your inventory logs will <b>not</b> be deleted.
                </li>
                <li>Your inventory logs will continue to show up in your compliance reports.</li>
              </>
            }
            showIcon
          />
        </div>
        <div style={{ marginTop: 10 }}>
          <Alert
            type='error'
            message='Sales and reporting'
            description={
              <>
                <hr />
                <li>Your sales will not be searchable.</li>
                <li>
                  Your sales history will <b>not</b> be deleted.
                </li>
              </>
            }
            showIcon
          />
        </div>
        {hasVariants && (
          <div style={{ marginTop: 10 }}>
            <Alert type='warning' message='All variants will also be deleted.' showIcon />
          </div>
        )}
        <div style={{ marginTop: 10 }}>
          <Alert
            type='warning'
            message='Are you sure you want to delete this product?'
            description={
              <>
                <hr />
                <p>{BLAZE_COMPANY} allows for flexible product configurations.</p>
                <p>
                  Please consider one of the following options first before fully deleting a
                  product.
                </p>
                <br />
                <img className='w-100' src={require('./product-options.png').default} />
              </>
            }
            showIcon
          />
        </div>
      </>
    )
  }

  render() {
    return (
      <Modal
        visible={this.props.isVisible}
        title={this.state.numberOfProductInventory ? <div>Error</div> : <div>Are you sure?</div>}
        footer={null}
        onCancel={() => {
          this.props.onClose()
        }}
      >
        {this.state.isCheckingInventory ? (
          <Skeleton active />
        ) : (
          <>
            <div>{this.props.productName}</div>
            {this.warningMessages}
            <div style={{ marginTop: 10 }}>
              <Button
                type='danger'
                disabled={this.state.numberOfProductInventory ? true : false}
                onClick={() => {
                  !this.state.numberOfProductInventory && this.props.submit()
                }}
                block={true}
              >
                Delete product
              </Button>
            </div>
          </>
        )}
      </Modal>
    )
  }
}
